import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ROLE,
  SIZE,
} from 'baseui/modal';
import formSetErrors from 'common/helpers/form-setError';
import notification from 'components/common/notification';
import { Form, Input } from 'components/elements';
import { Button } from 'components/elements/buttons';
import { FormState } from 'components/elements/form';
import useYupValidationResolver from 'hooks/use-yup-validation-resolver';
import BankAccountSelectInput from 'legacy-modules/master/bank-account/components/select-input';
import CashAccountSelectInput from 'legacy-modules/master/cash-account/components/select-input';
import {
  FullContainer,
  HalfContainer,
  SectionContainer,
} from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { useForm, useWatch } from 'react-hook-form';

import {
  ReceiveCommisionDialogProps,
  ReceiveCommisionFormType,
  ReceiveCommisionFormSchema,
} from './receive-commision-component';
import useCancelReceiveCommissionAction from '../hooks/use-cancel-receive-commission-action';

export enum PaymentMethodEnum {
  bank = 'bank',
  cash = 'cash',
}

export function ReceiveCommisionDialog(props: ReceiveCommisionDialogProps) {
  const { data } = props;
  const { t } = useTranslation();
  const defaultValues = React.useMemo<ReceiveCommisionFormType>(() => {
    const accountableType = (() => {
      switch (data?.agentCommission?.accountableType) {
        case 'CashAccount':
          return PaymentMethodEnum.cash;
        case 'BankAccount':
        default:
          return PaymentMethodEnum.bank;
      }
    })();
    return {
      accountableType,
      amount: data?.agentCommission?.amount || 0,
      acknowledgedAt: data?.agentCommission?.acknowledgedAt || new Date(),
      description: data?.agentCommission?.description || '',
      accountableId: data?.agentCommission?.accountable?.id || '',
    };
  }, [data]);

  const resolver = useYupValidationResolver(ReceiveCommisionFormSchema());

  const methods = useForm({
    defaultValues,
    resolver,
  });

  const onSubmit = React.useCallback(
    async (values: ReceiveCommisionFormType) => {
      try {
        props.onSubmit(values, methods);
      } catch (e) {
        if (e.errors) {
          formSetErrors(e.errors, methods.setError);
        }
        notification.error({ message: e.message });
      }
    },
    [methods, props],
  );

  const [accountableType] = useWatch({
    control: methods.control,
    name: ['accountableType'],
  });

  const acknowledgeComponent = React.useMemo(() => {
    return (
      <Input
        type="date-time"
        name="acknowledgedAt"
        label={t('common:acknowledged_at')}
        clearable={false}
        required
      />
    );
  }, [t]);

  const amountComponent = React.useMemo(() => {
    return (
      <Input type="number" label={t('common:amount')} name="amount" disabled />
    );
  }, [t]);

  const accountableTypeComponent = React.useMemo(() => {
    return (
      <Input
        type="select"
        label={t('common:account_type')}
        options={[
          {
            id: PaymentMethodEnum.bank,
            label: t('common:bank'),
          },
          {
            id: PaymentMethodEnum.cash,
            label: t('common:cash'),
          },
        ]}
        name="accountableType"
        clearable={false}
        required
      />
    );
  }, [t]);

  const descriptionComponent = React.useMemo(() => {
    return (
      <Input
        type="text-area"
        name="description"
        label={t('common:description')}
        placeholder={t('common:description')}
      />
    );
  }, [t]);

  const accountComponent = React.useMemo(() => {
    switch (accountableType) {
      case PaymentMethodEnum.cash:
        return (
          <CashAccountSelectInput
            showCreate={false}
            name="accountableId"
            label={t('common:account')}
            required
          />
        );
      case PaymentMethodEnum.bank:
        return (
          <BankAccountSelectInput
            showCreate={false}
            name="accountableId"
            label={t('common:account')}
            required
          />
        );
      default:
        return <></>;
    }
  }, [accountableType, t]);

  const { onCancel } = useCancelReceiveCommissionAction(props.data);

  return (
    <Modal
      closeable={false}
      isOpen
      animate
      // eslint-disable-next-line jsx-a11y/no-autofocus
      autoFocus={false}
      size={SIZE.auto}
      role={ROLE.dialog}
      overrides={{
        Root: {
          style: {
            zIndex: 300,
          },
        },
        Dialog: {
          style: {
            minWidth: '600px',
            display: 'flex',
            flexDirection: 'column',
          },
        },
      }}
    >
      <Form
        methods={methods}
        onSubmit={onSubmit}
        defaultEditable={!data.agentCommission?.accountable}
      >
        <ModalHeader>
          {t('common:receive_commission_extra', { extra: data.agent.name })}
        </ModalHeader>

        <ModalBody>
          <SectionContainer>
            <HalfContainer>{acknowledgeComponent}</HalfContainer>
            <HalfContainer>{amountComponent}</HalfContainer>
          </SectionContainer>
          <SectionContainer>
            <HalfContainer>{accountableTypeComponent}</HalfContainer>
            <HalfContainer>{accountComponent}</HalfContainer>
          </SectionContainer>
          <FullContainer>{descriptionComponent}</FullContainer>
        </ModalBody>
        <ModalFooter
          $style={{ display: 'flex', justifyContent: 'end', gap: '8px' }}
        >
          <FormState>
            {({ editable, setIsEditable }) => {
              return (
                <>
                  <Button
                    buttonType="tertiary"
                    onClick={props.onClose}
                    disabled={methods.formState.isSubmitting}
                  >
                    {t('common:close')}
                  </Button>
                  {editable ? (
                    <Input
                      type="submit"
                      text={t('common:confirm')}
                      hideIcon
                      onClick={() => {
                        methods.handleSubmit(onSubmit)();
                      }}
                    />
                  ) : (
                    <Button
                      buttonType="primary"
                      isError
                      onClick={() => onCancel(props.onClose)}
                    >
                      {t('common:cancel')}
                    </Button>
                  )}
                </>
              );
            }}
          </FormState>
        </ModalFooter>
      </Form>
    </Modal>
  );
}
